import React, { PureComponent } from "react";
import { Link } from "react-router";
import { changeLocation } from "api/Location";

import { loginRequest, batchRequest, getRequest } from "../api/ApiService";
import { fetchAppGeneralInfo, clearAppGeneralInfoCache, pickCourseAndGame } from "../api/AppGeneralInfoService";
import Auth from "../routes/Auth";
import Logo from "./assets/LandingPageLogo.png";
import EventEmitter from "../services/event-emitter";
import Teams from "../api/Teams";
import { detectMobile, getLogoStyle } from "services/utils";

// Turn a login-flow error into a user-facing message. Three buckets:
//   1. Bad credentials (only from the initial loginRequest — 400/401):
//      "Login details are incorrect, please try again."
//   2. Network / offline (no status code — browser couldn't reach the
//      server, timeout, DNS, CORS block, etc.):
//      "Can't reach the server. Check your internet connection..."
//   3. Anything else (server 5xx, or a downstream call failed after
//      credentials already validated):
//      "Something went wrong. Please try again..."
// `phase` is 'credentials' only for the initial loginRequest catch;
// every other catch runs AFTER login already succeeded, so 400/401
// there is not a credentials issue and shouldn't say so.
const classifyLoginError = (err, phase) => {
  const status = err && (err.status || (err.response && err.response.status));
  if (status == null) {
    return "Can't reach the server. Check your internet connection and try again.";
  }
  if (phase === 'credentials' && (status === 400 || status === 401)) {
    return "Login details are incorrect, please try again.";
  }
  return "Something went wrong. Please try again in a moment.";
};

class LoginPage extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      username: "",
      password: "",
      loginError: "",
      loginPlaceholder: "Login"
    };

    this.eventemitter = EventEmitter.getInstance();
    this.isMobile = detectMobile();
  }

  handleInputChange = e => {
    this.setState({
      loginError: "",
      [e.target.id]: e.target.value
    });
  };

  renderLoginError = () => {
    const { loginError } = this.state;

    if (loginError === "") {
      return null;
    }

    return (
      <div className="row text-center mg-top-3vh">
        <div className="col-md-4 col-md-offset-4 col-xs-12">
          <div className="alert alert-danger">
            <h4>{loginError}</h4>
          </div>
        </div>
      </div>
    );
  };

  renderActivationAlert = () => {
    const { location } = this.props;
    const { loginError } = this.state;

    if (loginError !== "") {
      return null;
    }

    if (location.query.activation) {
      return (
        <div className="row text-center mg-top-3vh">
          <div className="col-md-4 col-md-offset-4 col-xs-12">
            <div className="alert alert-info">
              <h4>
                We have sent a verify link to your email on file. Please check
                your email.
              </h4>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  renderVerifiedAlert = () => {
    const { location } = this.props;
    const { loginError } = this.state;

    if (loginError !== "") {
      return null;
    }

    if (location.query.verified) {
      return (
        <div className="row text-center mg-top-3vh">
          <div className="col-md-4 col-md-offset-4 col-xs-12">
            <div className="alert alert-info">
              <h4>
                Your account has been verified. You can login to continue
                working with Market Games!
              </h4>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  renderResetPasswordAlert = () => {
    const { location } = this.props;
    const { loginError } = this.state;

    if (loginError !== "") {
      return null;
    }

    if (location.query.reset_password) {
      return (
        <div className="row text-center mg-top-3vh">
          <div className="col-md-4 col-md-offset-4 col-xs-12">
            <div className="alert alert-info">
              <h4>
                Your password has been reset. You can login to continue working
                with Market Games!
              </h4>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  handleSubmit = () => {
    const { username, password } = this.state;
    const data = {
      username,
      password
    };

    this.setState({
      loginPlaceholder: "Login..."
    });

    loginRequest(data)
      .then(res => {
        const { token, user, position, is_paid } = res.body;

        // FE prefers the deterministic picker (highest-id course /
        // highest-id game) when the login response carries the plural
        // arrays — this matters for multi-course instructors. But for
        // students, BE currently returns only res.body.game (singular
        // id) and res.body.course (singular id), no plural arrays, so
        // the picker returns null and Auth would end up storing "null"
        // in sessionStorage → downstream game_id lookups return 0 →
        // /start bounces to /onboarding → wrong Round 1 landing.
        // Fall back to the singular ids when the picker gives us
        // nothing.
        const { course: pickedCourse, game: pickedGame } = pickCourseAndGame(
          res.body.courses,
          res.body.games,
        );
        const courseId = pickedCourse
          ? pickedCourse.id
          : (res.body.course || null);
        const game = pickedGame
          ? pickedGame.id
          : (res.body.game || null);

        console.log('[MG:login] response body:', {
          user, position, is_paid,
          courses: res.body && res.body.courses,
          games: res.body && res.body.games,
          pickedCourse, pickedGame,
        });
        console.log('[MG:login] storing in sessionStorage:', { course_id: courseId, game_id: game });

        Auth.authenticateUser(token, user, position, game, courseId);

        // Nuke any cached general-info from a previous session in this
        // tab. Otherwise a stale response (e.g. from before backend
        // added the intro field) can bypass the intro gate.
        clearAppGeneralInfoCache();

        const joinRequest1 = batchRequest([Auth.getUserDataRequest()]);

        joinRequest1
          .then(res => {
            if (res[0].statusCode !== 200) {
              throw res[0];
            }

            const user_data = res[0].body;
            Auth.setUserData(user_data);

            if (position === "Student") {
              // Fetch general-info directly here so we can synchronously
              // decide on the intro gate. The get_info endpoint used
              // above may not include the intro field, so we can't rely
              // on user_data alone.
              fetchAppGeneralInfo(game, "student").then(gi => {
                const freshUser = (gi && gi.user) || user_data;
                if (freshUser) {
                  Auth.setUserData(freshUser);
                }

                // Intro gate: first-time students must watch the ITB
                // intro video before continuing. Intro flag is a 0/1
                // field on the user record; 0 means "not yet watched".
                if (freshUser && freshUser.intro === 0) {
                  Teams.setEmptyData();
                  this.eventemitter.trigger("reload-data");
                  changeLocation("/intro-video");
                  return;
                }

                // Kick off the App-level refresh so the rest of the app
                // has fresh state, but we don't need to wait for it.
                this.eventemitter.trigger("reload-data");

                if (is_paid === false) {
                  Teams.setEmptyData();

                  if (this.isMobile === true) {
                    changeLocation("/promo-code");
                  } else {
                    changeLocation(`/start`);
                  }
                } else if (!game) {
                  // Picker returned no game for the current course:
                  // instructor hasn't spun up the exercise yet. Route
                  // to /onboarding — App.js's fetchData will set
                  // noGameYet and OnboardingPage will render the
                  // locked "Waiting on Instructor" screen. Skipping
                  // the /join branch here avoids a flash of the join
                  // page for a student who is already in a course.
                  Teams.setEmptyData();
                  changeLocation("/onboarding");
                } else if (Auth.getRole() === "" || !Auth.getTeamId()) {
                  Teams.setEmptyData();
                  changeLocation("/join");
                } else {
                  const joinRequest2 = batchRequest([
                    ...Teams.requests(),
                  ]);

                  joinRequest2
                    .then(res2 => {
                      const teams_data = [
                        {
                          status: res2[0].statusCode,
                          data: res2[0].body
                        },
                        {
                          status: res2[1].statusCode,
                          data: res2[1].body
                        }
                      ];
                      const teams_status = Teams.setData(teams_data);
                      if (teams_status === "Error") {
                        throw teams_status;
                      }

                      getRequest(`${process.env.REACT_APP_API_URL}v2/games/teamplayers/?game=${Auth.getGameId()}`)
                        .then(res3 => {
                          Auth.setPlayerRoles(res3.body.results);
                          changeLocation(`/start`);
                        })
                        .catch(err3 => {
                          console.log("Error3", err3);
                          Auth.deauthenticateUser();

                          this.setState({
                            loginPlaceholder: "Login",
                            loginError: classifyLoginError(err3),
                          });
                        });
                    })
                    .catch(err1 => {
                      console.log("Error1", err1);
                      Auth.deauthenticateUser();

                      this.setState({
                        loginPlaceholder: "Login",
                        loginError: classifyLoginError(err1),
                      });
                    });
                }
              }).catch(err => {
                console.log("Error general-info", err);
                Auth.deauthenticateUser();
                this.setState({
                  loginPlaceholder: "Login",
                  loginError: classifyLoginError(err),
                });
              });
            } else if (
              position === "Instructor"
              || position === "Co-Instructor"
              || position === "Instructor Assistant"
            ) {
              Teams.setEmptyData();
              // Mirror the student branch: refresh Auth._user from
              // general-info's data.user before navigating to /start.
              // /users/<id>/get_info/ has been observed to return a
              // stale `onboard` (0) even after the BE has flipped it,
              // which routes already-onboarded instructors back into
              // /instructor/onboarding/intro. general-info sources
              // user from a different path and reflects current state,
              // so letting it override closes the gap. Falls back to
              // the get_info body if general-info can't be fetched.
              fetchAppGeneralInfo(0, "instructor")
                .then(gi => {
                  const freshUser = (gi && gi.user) || user_data;
                  if (freshUser) {
                    Auth.setUserData(freshUser);
                  }
                })
                .catch(() => {
                  // Non-fatal: fall through with the get_info value.
                })
                .then(() => {
                  this.eventemitter.trigger("reload-data", [() => {
                    changeLocation("/start");
                  }]);
                });
            } else {
              throw res[0];
            }
          })
          .catch(err2 => {
            console.log("Error2", err2);
            Auth.deauthenticateUser();

            this.setState({
              loginPlaceholder: "Login",
              loginError: classifyLoginError(err2),
            });
          });
      })
      .catch(err => {
        console.log("Error3", err);
        this.setState({
          loginPlaceholder: "Login",
          loginError: classifyLoginError(err, 'credentials'),
        });
      });
  };

  onSubmit = e => {
    e.preventDefault();
  };

  render() {
    const { username, password, loginPlaceholder } = this.state;
    const logoStyle = getLogoStyle(this.isMobile);
    const loginError = this.renderLoginError();
    const activationAlert = this.renderActivationAlert();
    const verifiedAlert = this.renderVerifiedAlert();
    const resetPasswordAlert = this.renderResetPasswordAlert();
    const formClass = this.isMobile ? "mg-top-5vh" : "mg-top-10vh";

    return (
      <div className="container-fluid bg-color-blue height-100vh">
        <div className="text-center mg-top-15vh">
          <Link to="/welcome">
            <img src={Logo} style={logoStyle} alt="Logo" />
          </Link>
        </div>
        {loginError}
        {activationAlert}
        {verifiedAlert}
        {resetPasswordAlert}
        <div className={formClass}>
          <form className="form-signin" onSubmit={this.onSubmit}>
            <div className="row">
              <div className="form-group col-md-4 col-md-offset-2 col-xs-12">
                <label
                  htmlFor="username"
                  className="control-label font-size-14 color-white"
                >
                  Username
                </label>
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={this.handleInputChange}
                  className="form-control border-radius-none"
                  placeholder="Username"
                  required={true}
                  autoFocus={!this.isMobile}
                />
              </div>
              <div className="form-group col-md-4 col-xs-12">
                <label
                  htmlFor="password"
                  className="control-label font-size-14 color-white"
                >
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={this.handleInputChange}
                  className="form-control border-radius-none"
                  placeholder="Password"
                  required={true}
                />
                <Link
                  to="/reset-password"
                  className="btn btn-link reset-password-link"
                >
                  Reset password
                </Link>
              </div>
            </div>
            <div className="row mg-top-3vh">
              <div className="col-md-2 col-md-offset-8 col-xs-12">
                <button
                  type="submit"
                  onClick={this.handleSubmit}
                  className="btn login-btn btn-lg btn-block"
                >
                  {loginPlaceholder}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    );
  }
}

export default LoginPage;



// WEBPACK FOOTER //
// ./src/containers/login-page.js